import { DatePipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { AlertService } from '@context/frontend/alert';
import {
	DialogBase,
	DialogOptions,
	DialogService,
} from '@context/frontend/common';
import {
	ContentService,
	SelectContentDialogComponent,
} from '@context/frontend/content';
import { applyValidators } from '@context/frontend/form';
import {
	PaginatedObject,
	PaginatedPageItem,
} from '@context/frontend/pagination';
import { DynamicEntityListComponent } from '@context/frontend/ui/components';
import {
	ApplicationType,
	ApplicationTypeOption,
	Chemical,
	ChemicalOption,
	ChemicalRevision,
	ChemicalRevisionOption,
	ChemicalUsageLog,
} from '@context/shared/types/chemical';
import { Content, EntityOption } from '@context/shared/types/common';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiDropdownSheet } from '@taiga-ui/addon-mobile';
import { TuiDay, TuiTime } from '@taiga-ui/cdk';
import {
	TuiButton,
	TuiDataList,
	TuiError,
	TuiFilterByInputPipe,
	TuiHint,
	TuiIcon,
	TuiInput,
	TuiLoader,
	TuiNotification,
} from '@taiga-ui/core';
import {
	TuiButtonLoading,
	TuiComboBox,
	TuiDataListWrapper,
	TuiInputDateTime,
	TuiSelect,
	TuiTextarea,
} from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import {
	collection,
	doc,
	DocumentReference,
	Timestamp,
	where,
} from 'firebase/firestore';
import { ChemicalRowComponent } from '../../components';
import { ApplicationTypeService } from '../../services/application-type.service';
import { ChemicalRevisionService } from '../../services/chemical-revision.service';
import { ChemicalUsageLogService } from '../../services/chemical-usage-log.service';
import { ChemicalService } from '../../services/chemical.service';

export type ChemicalUsageLogFormDialogData = {
	chemicalId?: string;
	fileId?: string;
};

@Component({
	imports: [
		TranslocoModule,
		TuiButton,
		TuiIcon,
		TuiHint,
		ReactiveFormsModule,
		TuiInput,
		TuiError,
		TuiButtonLoading,
		TuiSelect,
		TuiComboBox,
		TuiDataList,
		TuiDataListWrapper,
		TuiFilterByInputPipe,
		TuiLoader,
		ChemicalRowComponent,
		FormsModule,
		TuiInputDateTime,
		TuiDropdownSheet,
		DynamicEntityListComponent,
		DatePipe,
		TuiTextarea,
		TuiNotification,
	],
	selector: 'ctx-chemical-usage-log-form-dialog',
	templateUrl: './chemical-usage-log-form-dialog.component.html',
	styleUrl: './chemical-usage-log-form-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChemicalUsageLogFormDialogComponent
	extends applyValidators(
		DialogBase<
			ChemicalUsageLog | null,
			ChemicalUsageLogFormDialogData | undefined
		>,
	)
	implements OnInit
{
	static override readonly DialogOptions = {
		label: 'chemical.create-chemical-log',
		size: 'm',
		dismissible: false,
	} as DialogOptions;

	private readonly chemicalService = inject(ChemicalService);
	private readonly chemicalRevisionService = inject(ChemicalRevisionService);
	private readonly applicationTypeService = inject(ApplicationTypeService);
	private readonly usageLogService = inject(ChemicalUsageLogService);
	private readonly alert = inject(AlertService);
	private readonly dialog = inject(DialogService);
	private readonly contentService = inject(ContentService);
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly destroyRef = inject(DestroyRef);

	form: FormGroup | null = null;

	readonly busy = signal(false);
	readonly loading = signal(false);

	readonly chemicalOptions = signal<ChemicalOption[]>([]);
	readonly revisionOptions = signal<ChemicalRevisionOption[]>([]);
	readonly noActiveRevisions = signal(false);
	readonly applicationTypeOptions = signal<ApplicationTypeOption[]>([]);

	readonly selectedCrops = signal<DocumentReference<Content>[]>([]);
	readonly selectedCropData = signal<Map<string, Content>>(new Map());

	readonly cropListItems = computed(() => {
		const crops = this.selectedCropData();
		return this.selectedCrops()
			.map((ref) => {
				const crop = crops.get(ref.id);
				return crop
					? {
							id: ref.id,
							name: crop.name,
							ref,
							data: crop,
						}
					: null;
			})
			.filter((item): item is NonNullable<typeof item> => item !== null);
	});

	readonly maxDate = computed(() => {
		const now = new Date();
		return TuiDay.fromLocalNativeDate(now);
	});

	async ngOnInit() {
		this.loading.set(true);
		try {
			await this.fetchChemicals();
			await this.setupForm();
			await this.applyPrefillsFromContext();
		} catch (error) {
			console.error('Error setting up chemical usage log form', error);
			this.onDismiss(null);
		}
		this.loading.set(false);
	}

	private async applyPrefillsFromContext() {
		const chemicalId = this.context.data?.chemicalId;
		const fileId = this.context.data?.fileId;

		if (chemicalId) {
			const option = this.chemicalOptions().find(
				(o) => o.ref?.id === chemicalId || o.data.id === chemicalId,
			);
			if (option) {
				this.form?.patchValue({ chemical: option });
			}
		}

		if (fileId && this.form) {
			const item = await this.contentService.findById(fileId);
			const existing = (this.form.get('crops')?.value ??
				[]) as DocumentReference<Content>[];
			const already = existing.some((r) => r.id === item.id);
			const refs = already ? existing : [...existing, item.ref];
			const data = new Map(this.selectedCropData());
			data.set(item.id, item.data() as Content);
			this.selectedCropData.set(data);
			this.selectedCrops.set(refs);
			this.form.patchValue({ crops: refs });
		}

		this.cdRef.markForCheck();
	}

	async fetchChemicals() {
		const paginated = new PaginatedObject<Chemical>({
			pageSize: null,
		});
		const result = await this.chemicalService.fetch(paginated);
		const options = result.items.map(
			(item) => new ChemicalOption(item.data, item.ref),
		);
		this.chemicalOptions.set(options);
	}

	async onChemicalSelected(chemicalOption: EntityOption<Chemical> | null) {
		if (!chemicalOption) {
			this.revisionOptions.set([]);
			this.noActiveRevisions.set(false);
			this.applicationTypeOptions.set([]);
			this.form?.patchValue({
				chemicalRevision: null,
				applicationType: null,
			});
			return;
		}

		const chemicalRef =
			chemicalOption.valueOf() as DocumentReference<Chemical>;
		if (!chemicalRef) return;

		const paginated = new PaginatedObject<ChemicalRevision>({
			parentRef: chemicalRef,
			pageSize: null,
			constraints: [where('expiresAt', '>', Timestamp.now())],
		});

		const result = await this.chemicalRevisionService.fetch(paginated);
		const options = result.items.map(
			(item) => new ChemicalRevisionOption(item.data, item.ref),
		);
		this.revisionOptions.set(options);
		this.noActiveRevisions.set(options.length === 0);

		this.applicationTypeOptions.set([]);

		const revisionControl = this.form?.get('chemicalRevision');
		if (options.length === 0) {
			revisionControl?.disable();
		} else {
			revisionControl?.enable();
		}

		// When a chemical has a single active registration number, auto-select
		// it to save the user a click. Otherwise leave it blank to force a choice.
		this.form?.patchValue({
			chemicalRevision: options.length === 1 ? options[0] : null,
			applicationType: null,
		});

		// This runs after an awaited fetch on an OnPush dialog; without an explicit
		// tick the combobox can fail to reflect the new value/options until the next
		// interaction (the "first selection does nothing, second works" symptom).
		this.cdRef.markForCheck();
	}

	async onRevisionSelected(revisionOption: ChemicalRevisionOption | null) {
		if (!revisionOption) {
			this.applicationTypeOptions.set([]);
			this.form?.patchValue({ applicationType: null });
			return;
		}

		const revisionRef =
			revisionOption.valueOf() as DocumentReference<ChemicalRevision>;
		if (!revisionRef) return;

		const applicationTypes =
			await this.applicationTypeService.getApplicationTypes(revisionRef);
		const appTypeCollection = collection(
			revisionRef,
			ApplicationTypeService.CollectionId,
		);
		const options = applicationTypes.map((appType) => {
			const appTypeRef = doc(
				appTypeCollection,
				appType.id,
			) as DocumentReference<ApplicationType>;
			return new ApplicationTypeOption(appType, appTypeRef);
		});
		this.applicationTypeOptions.set(options);

		this.form?.patchValue({ applicationType: null });

		this.cdRef.markForCheck();
	}

	onApplicationTypeSelected(
		applicationTypeOption: ApplicationTypeOption | null,
	) {
		if (!applicationTypeOption) {
			this.form?.patchValue({ reiHours: null });
			return;
		}

		const reiHours = applicationTypeOption.data.reiHours;
		this.form?.patchValue({ reiHours });
	}

	async setupForm() {
		const now = new Date();
		const defaultDate = TuiDay.fromLocalNativeDate(now);
		const defaultTime = new TuiTime(now.getHours(), now.getMinutes());

		this.form = new FormGroup({
			chemical: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.chemical.required',
				),
			),
			chemicalRevision: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.chemical-revision.required',
				),
			),
			applicationType: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.application-type.required',
				),
			),
			location: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.location.required',
				),
			),
			note: new FormControl<string | null>(null),
			reiHours: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.rei-hours.required',
				),
			),
			appliedAt: new FormControl(
				[defaultDate, defaultTime],
				[
					(field) =>
						this.getRequiredValidator(
							field,
							'form.error.applied-at.required',
						),
					(field) =>
						this.getFutureDateValidator(
							field,
							'form.error.applied-at.future-date',
						),
				],
			),
			crops: new FormControl([]),
		});

		this.form
			.get('chemical')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				if (value) this.form?.get('chemicalRevision')?.enable();
				else {
					this.form?.get('chemicalRevision')?.disable();
					this.form?.get('applicationType')?.disable();
					this.form?.get('reiHours')?.disable();
				}

				void this.onChemicalSelected(value);
				this.form?.patchValue({
					chemicalRevision: null,
					applicationType: null,
					reiHours: null,
				});
			});

		this.form
			.get('chemicalRevision')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				if (value) this.form?.get('applicationType')?.enable();
				else {
					this.form?.get('applicationType')?.disable();
					this.form?.get('reiHours')?.disable();
				}

				void this.onRevisionSelected(value);
				this.form?.patchValue({
					applicationType: null,
					reiHours: null,
				});
			});

		this.form
			.get('applicationType')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.onApplicationTypeSelected(value);
			});

		this.form?.get('chemicalRevision')?.disable();
		this.form?.get('applicationType')?.disable();
		this.form?.get('reiHours')?.disable();
	}

	readonly onSelectCrops = () => {
		this.dialog
			.open<PaginatedPageItem<Content>[]>(
				new PolymorpheusComponent(SelectContentDialogComponent),
				SelectContentDialogComponent.Options({
					data: {
						type: 'file',
						multiSelect: true,
						initialSelection: this.selectedCrops(),
					},
					label: 'chemical.usage-log.crops',
				}),
			)
			.subscribe({
				next: async (selected) => {
					if (!selected) return;

					const data = new Map<string, Content>();
					const refs = new Map<string, DocumentReference<Content>>();
					selected.forEach((item) => {
						data.set(item.ref.id, item.data);
						refs.set(item.ref.id, item.ref);
					});

					const selectedRefs = Array.from(refs.values());
					this.form?.patchValue({ crops: selectedRefs });

					this.selectedCrops.set(selectedRefs);
					this.selectedCropData.set(data);
					this.cdRef.markForCheck();
				},
			});
	};

	readonly onRemoveCrop = (item: {
		id: string;
		ref: DocumentReference<Content>;
	}) => {
		const currentCrops = this.selectedCrops();
		const newCrops = currentCrops.filter((ref) => ref.id !== item.id);
		this.selectedCrops.set(newCrops);
		this.form?.patchValue({ crops: newCrops });

		const data = this.selectedCropData();
		data.delete(item.id);
		this.selectedCropData.set(new Map(data));
	};

	onSubmit() {
		if (!this.form || this.form.invalid) return;
		this.form.markAllAsTouched();

		if (this.busy()) return;
		this.busy.set(true);

		this.form.disable({ emitEvent: false });

		const value = this.form.getRawValue();

		const { applicationType, chemicalRevision, chemical } = value;
		const chemicalRef = chemical.valueOf();
		const revisionRef = chemicalRevision.valueOf();
		const applicationTypeRef = applicationType.valueOf();

		const [dateValue, timeValue] = value.appliedAt as [TuiDay, TuiTime];
		const date = dateValue.toLocalNativeDate();
		date.setHours(timeValue.hours, timeValue.minutes, 0, 0);

		this.usageLogService
			.create(chemicalRef, {
				chemical: chemicalRef,
				chemicalRevision: revisionRef,
				applicationType: applicationTypeRef,
				location: value.location,
				note: value.note ?? undefined,
				crops: value.crops || [],
				reiHours: Number(value.reiHours),
				appliedAt: Timestamp.fromDate(date),
			})
			.then((created) => {
				this.onDismiss(created);
				this.alert.open('generic-success').subscribe();
			})
			.catch((error) => {
				console.error(
					'There was an issue creating the usage log',
					error,
				);
				this.alert.open('generic-error').subscribe();
			})
			.finally(() => {
				this.form?.enable();
				this.busy.set(false);
			});
	}
}
